<template>
  <div class="wrapper" data-step="12">
    <div class="results-tab">
      <div class="data-card card">
        <div class="data-container">
          <div class="header">
            <div class="content">
              <div class="title">Résultats de la campagne</div>
              <p>
                Vous trouverez ici tous les résultats des évaluations terminées
                par vos talents
              </p>
            </div>
            <div class="tabs">
              <div
                class="tab"
                :class="{ current: !ponderationIsVisible }"
                @click="ponderationIsVisible = false"
              >
                Vue globale
              </div>
              <div
                class="tab"
                :class="{ current: ponderationIsVisible }"
                @click="ponderationIsVisible = true"
              >
                Pondération
              </div>
            </div>
            <Button
              class="outline icon square expend-btn"
              :icon="
                state.fullScreen
                  ? 'bi-arrows-angle-contract'
                  : 'bi-arrows-angle-expand'
              "
              v-tooltip="
                state.fullScreen
                  ? 'Affichage classique'
                  : 'Affichage plein écran'
              "
              @click="toggleFullScreen"
            />
          </div>
          <transition name="fade" mode="out-in">
            <Table
              v-if="!ponderationIsVisible && resultsAreComputed"
              data-step="13"
              :data="participantStore.finishedParticipants"
              :fields="state.fields"
              :search="search"
              :bulk="
                !campaignStore.currentCampaign.archived
                  ? {
                      actions: [
                        {
                          icon: 'bi-arrow-left-right',
                          label: 'Déplacer les talents',
                          action: (participants) => {
                            openActionModal([...participants], 'move');
                          },
                          display: () => {
                            return true;
                          },
                        },
                        {
                          class: 'red-color',
                          icon: 'bi-trash',
                          label: 'Supprimer les talents',
                          action: (participants) => {
                            openActionModal([...participants], 'delete');
                          },
                          display: () => {
                            return true;
                          },
                        },
                      ],
                    }
                  : null
              "
              @clickOnRow="openCandidatePanel"
            >
              <template #[`results.compatibility`]="{ data }">
                <ProgressCircular :value="data.results.compatibility" />
              </template>
              <template #[`candidate.last_name`]="{ data }">
                <div class="container">
                  <span
                    v-html="
                      `${data.candidate.first_name} ${data.candidate.last_name}`
                    "
                  />
                </div>
              </template>
              <template
                v-for="skill in modules.filter((module) => {
                  if (campaignVersion.value === 1) return module.id_v2;
                  else return !module.disabled;
                })"
                :key="skill.id_v2 ?? skill.id"
                #[`results.${getModuleId(skill,campaignVersion)}.${getValueIdentifier(skill.type)}`]="{
                  data,
                }"
              >
                <div class="container">
                  <span
                    class="skill-notation"
                    :class="data.results[getModuleId(skill, campaignVersion)].rank"
                    v-if ="skill.type === 'skill'"
                  >
                    {{ data.results[getModuleId(skill, campaignVersion)].rank }}
                  </span>
                  <span class="skill-notation style" v-else>
                    {{ getStyleLabel(data.results[getModuleId(skill, campaignVersion)], skill.id) }}
                  </span>
                </div>
              </template>
              <template #actions="actionsProps">
                <div @click.stop="null">
                  <VDropdown
                    :distance="10"
                    :disabled="campaignStore.currentCampaign.archived"
                    placement="left"
                  >
                    <div
                      class="btn outline secondary icon square"
                      :class="{
                        disabled: campaignStore.currentCampaign.archived,
                      }"
                    >
                      <i class="bi bi-three-dots-vertical" />
                    </div>
                    <template #popper>
                      <div
                        class="action red-color"
                        v-close-popper
                        @click="openActionModal([actionsProps.row], 'delete')"
                      >
                        <i class="bi bi-trash" />
                        <span>Supprimer le talent</span>
                      </div>
                      <div class="separator" />
                      <div
                        class="action"
                        v-close-popper
                        @click="openActionModal([actionsProps.row], 'move')"
                      >
                        <i class="bi bi-arrow-left-right" />
                        <span>Déplacer le talent</span>
                      </div>
                    </template>
                  </VDropdown>
                </div>
              </template>
              <template #empty>
                <Empty
                  message="Aucun résultat pour le moment"
                  :action="{
                    label: 'Voir les talents en cours d\'évaluation',
                    click: () => {
                      appStore.currentCampaignTab = 'Activated';
                    },
                  }"
                  :dark="isDark"
                  icon="loop"
                />
              </template>
            </Table>
            <Ponderation v-else />
          </transition>
        </div>
      </div>
    </div>
    <ParticipantModal
      :isOpen="state.showParticipantModal"
      @close="state.showParticipantModal = !state.showParticipantModal"
    />
    <CandidatePanel
      :class="{ open: candidatePanelIsOpen }"
      :currentParticipant="currentParticipant"
      @close="candidatePanelIsOpen = false"
      @preview="state.previewIsOpen = true"
    />
    <DocumentPreview
      :isOpen="state.previewIsOpen"
      :candidate="currentParticipant"
      @close="state.previewIsOpen = false"
    />
    <ActionModal
      :modal="state.actionModal.modal"
      :data="state.participants"
      :isOpen="state.actionModal.show"
      @close="state.actionModal.show = false"
      @confirm="state.actionModal.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useModulesStore } from "@/stores/modules.js";
import { useAppStore } from "@/stores/app.js";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";
import ParticipantModal from "@/components/modals/app/participantModal/ParticipantModal.vue";
import CandidatePanel from "../candidatePanel/CandidatePanel.vue";
import Empty from "@/components/table/Empty.vue";
import Ponderation from "../Ponderation.vue";
import DocumentPreview from "@/components/pdf/Document.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";

import type { LeadershipStyle } from "@/views/game/resultV2/leadership_style"

const isDark = useDark();

const route = useRoute();

const appStore = useAppStore();

const emits = defineEmits("toggleFullscreen");

const { candidatePanelIsOpen, ponderationIsVisible } = storeToRefs(appStore);

const modulesStore = useModulesStore();

const { modules } = storeToRefs(modulesStore);

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const participantStore =
  route.name === "campaign" ? useParticipantStore() : useOnboardingStore();

const { currentParticipant } = storeToRefs(participantStore);

defineProps({
  search: {
    type: Object,
  },
});

const state = reactive({
  fullScreen: false,
  showParticipantModal: false,
  previewIsOpen: false,
  participants: [],
  actionModal: {
    show: false,
    modal: "",
  },
  fields: [
    {
      id: "results.compatibility",
      label: "Compatibilité",
      defaultSortDir: "DESC",
      center: true,
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "candidate.last_name",
      label: "Talent",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ],
});

const campaignCalibrationID = computed(() => {
  if (!campaignStore.currentCampaign) return null;

  return campaignStore.currentCampaign.calibration_id;
});

const campaignVersionObject = computed<CampaignVersion | null>(() => {
  return getVersionObjectFromVersionID(campaignCalibrationID.value);
});

const resultsAreComputed = computed(() => {
  if (route.name === "campaign") {
    return campaignStore.currentCampaign.resultsAreComputed;
  } else {
    return true;
  }
});

const campaignVersion = computed(() => {
  return campaignStore.currentCampaign.version;
});

modules.value
  .filter((module) => {
    if (campaignVersion.value === 1) return module.id_v2;

    if (module.disabled) return false;

    if (campaignVersionObject.value?.duration === 25) {
      switch (module.id) {
        case "MS_ADPT":
        case "MS_GDSS":
        case "MS_RDPB":
        case "planning":
        case "time_manage":
          return false;
        default:
          return true;
      }
    }

    if (module.id === "planning") return campaignStore.isV2_1(campaignStore.currentCampaign);

    if (module.id === "leadership") return false;
    if (module.id === "learn_strategy") return false;
    if (module.id === "motivation") return false;

    return true;
  })
  .sort((a, b) => {
    const getPriority = (skill) => {
      if (
        campaignStore.currentCampaign.skills_mandatory
          .split(";")
          .includes(skill)
      )
        return 1;
      if (
        campaignStore.currentCampaign.skills_important
          .split(";")
          .includes(skill)
      )
        return 2;
      if (
        campaignStore.currentCampaign.skills_secondary
          .split(";")
          .includes(skill)
      )
        return 3;

      return 4;
    };

    return getPriority(a.id_v2 ?? a.id) - getPriority(b.id_v2 ?? b.id);
  })
  .forEach((skill) => {
    const moduleId = getModuleId(skill, campaignVersion.value);
    const value = getValueIdentifier(skill.type);
    const id = `results.${moduleId}.${value}`;

    state.fields.push({
      label: skill.name,
      id,
      center: true,
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (skill.type === "style") {
          if (dir === "ASC") return b.localeCompare(a);
          return a.localeCompare(b);
        }

        if (dir === "ASC") return a - b;
        return b - a;
      },
    });
  });

if (route.name === "campaign" && !resultsAreComputed.value) {
  participantStore.computeResults();
}

function toggleFullScreen() {
  state.fullScreen = !state.fullScreen;

  emits("toggleFullscreen");
}

function getValueIdentifier(type) {
  return type === "skill" ? "note" : "rank";
}

function getModuleId(module, version) {
  return version === 1
    ? module.id
    : `${module.id_v2 ?? module.id}_${
        module.type === "skill" ? "score" : "style"
      }`;
}

function openCandidatePanel(candidate) {
  currentParticipant.value = candidate;

  if (campaignVersion.value === 1) {
    // Set badges as seen by candidates (give them a badge rank from 1 being best 3 worst)
    const rdpb_score = currentParticipant.value.results.MS_RDPB.score;
    const gdss_score = currentParticipant.value.results.MS_GDSS.score;
    const adpt_score = currentParticipant.value.results.MS_ADPT.score;

    if (rdpb_score >= gdss_score && gdss_score >= adpt_score) {
      //Architect Profil
      currentParticipant.value.results.MS_RDPB.badge_rank = 1;
      currentParticipant.value.results.MS_GDSS.badge_rank = 2;
      currentParticipant.value.results.MS_ADPT.badge_rank = 3;
    } else if (rdpb_score >= adpt_score && adpt_score >= gdss_score) {
      //Inventor Profil
      currentParticipant.value.results.MS_RDPB.badge_rank = 1;
      currentParticipant.value.results.MS_ADPT.badge_rank = 2;
      currentParticipant.value.results.MS_GDSS.badge_rank = 3;
    } else if (gdss_score >= rdpb_score && rdpb_score >= adpt_score) {
      //Minesweeper Profil
      currentParticipant.value.results.MS_GDSS.badge_rank = 1;
      currentParticipant.value.results.MS_RDPB.badge_rank = 2;
      currentParticipant.value.results.MS_ADPT.badge_rank = 3;
    } else if (gdss_score >= adpt_score && adpt_score >= rdpb_score) {
      //Pilot  Profil
      currentParticipant.value.results.MS_GDSS.badge_rank = 1;
      currentParticipant.value.results.MS_ADPT.badge_rank = 2;
      currentParticipant.value.results.MS_RDPB.badge_rank = 3;
    } else if (adpt_score >= rdpb_score && rdpb_score >= gdss_score) {
      //Navigator  Profil
      currentParticipant.value.results.MS_ADPT.badge_rank = 1;
      currentParticipant.value.results.MS_RDPB.badge_rank = 2;
      currentParticipant.value.results.MS_GDSS.badge_rank = 3;
    } else if (adpt_score >= gdss_score && gdss_score >= rdpb_score) {
      //Balancer Profil
      currentParticipant.value.results.MS_ADPT.badge_rank = 1;
      currentParticipant.value.results.MS_GDSS.badge_rank = 2;
      currentParticipant.value.results.MS_RDPB.badge_rank = 3;
    }
  }

  candidatePanelIsOpen.value = true;
}

function closePanel(e) {
  if (!candidatePanelIsOpen.value) return;

  const tableWrapperElt = document.querySelector(".table-wrapper");
  const panelElt = document.querySelector(".candidate-panel");

  if (!tableWrapperElt.contains(e.target) && !panelElt.contains(e.target)) {
    if (!e.target.closest(".document")) candidatePanelIsOpen.value = false;
  }
}

function openActionModal(participants, modal) {
  state.participants = participants;
  state.actionModal = {
    show: true,
    modal,
  };
}

function getLeadershipStyleLabel(data) {
  if (!data || !data.value) return null;

  const leadership_strategies = Object.values(modules.value).find((elt) => elt.id === "leadership");

  const leadership_styles: LeadershipStyle = JSON.parse(data.value);

  return leadership_strategies.result_wordings?.[leadership_styles.leading_style]?.label;
};

function getLearnStrategyStyleLabel(data) {
  if (!data || !data.value) return null;

  // Get all the learn strategies
  const learn_strategies = Object.values(modules.value).find((elt) => elt.id === "learn_strategy");

  // Get the strategy of the current candidate
  const strategy = Object.values(learn_strategies.result_wordings).find((elt) => elt.id === data.value);

  return strategy?.label;
}

function getMotivationStyleLabel(data) {
  if (!data) return null;

  // Get all the motivation texts
  const motivationTexts = Object.values(modules.value).find((elt) => elt.id === "motivation");

  // Get the text for the current candidate
  return motivationTexts?.result_wordings?.[data]?.label;
}

const getStyleLabel = (style, skillID) => {
  switch (skillID) {
    case "leadership":
      return getLeadershipStyleLabel(style);
    case "learn_strategy":
      return getLearnStrategyStyleLabel(style);
    case "motivation":
      return getMotivationStyleLabel(style);
    default:
      break;
  }
  return style;
};

onMounted(() => {
  if (appStore.isOnboarded) {
    document.addEventListener("click", closePanel);
  }
});

onBeforeUnmount(() => {
  if (appStore.isOnboarded) {
    appStore.candidatePanelIsOpen = false;
    document.removeEventListener("click", closePanel);
  }
});
</script>
